import { locale, _ } from 'svelte-i18n';
import { get } from 'svelte/store';
import type { Category, SubCategory } from './api';
import { categoriesStore } from './stores/categoriesStore';

/** Technical category key for the catch-all fallback category. */
const CATCH_ALL_CATEGORY = 'miscellaneous';

/** Technical subcategory key used when no specific subcategory matched. */
const CATCH_ALL_SUBCATEGORY = 'other';

/**
 * Returns true when the classification pair represents the catch-all fallback
 * (category="miscellaneous", subCategory="other").  In this case callers should
 * display a single locale-resolved label instead of a "Category › SubCategory"
 * compound string.
 */
export function isCatchAllClassification(
  categoryTechnicalName: string,
  subCategoryTechnicalName: string | null | undefined,
): boolean {
  return (
    categoryTechnicalName === CATCH_ALL_CATEGORY &&
    subCategoryTechnicalName === CATCH_ALL_SUBCATEGORY
  );
}

/**
 * Get the display name for a category in the current locale
 * Chooses between displayName (German) and displayNameEn (English)
 * based on the current svelte-i18n locale
 */
export function getLocalizedCategoryName(category: Category): string {
  const currentLocale = get(locale);

  // If English locale and English name is available, use it
  if (currentLocale?.startsWith('en') && category.displayNameEn) {
    return category.displayNameEn;
  }

  // Default to German name
  return category.displayName;
}

/**
 * Get the display name for a subcategory in the current locale
 */
export function getLocalizedSubcategoryName(subcategory: SubCategory): string {
  const currentLocale = get(locale);

  // If English locale and English name is available, use it
  if (currentLocale?.startsWith('en') && subcategory.displayNameEn) {
    return subcategory.displayNameEn;
  }

  // Default to German name
  return subcategory.displayName;
}

/**
 * Localize a list of categories for the current user
 * Returns a copy with displayName set to the appropriate language
 * while preserving the original data structure
 */
export function localizeCategories(categories: Category[]): Category[] {
  return categories.map((category) => ({
    ...category,
    displayName: getLocalizedCategoryName(category),
    subCategories:
      category.subCategories?.map((sub) => ({
        ...sub,
        displayName: getLocalizedSubcategoryName(sub),
      })) || [],
  }));
}

/**
 * Get localized category name from a classification
 * categoryTechnicalName is like "housing-property"
 * Pass currentLocale from the template ($locale) to ensure Svelte reactivity
 */
export function getLocalizedClassificationCategoryName(
  categoryTechnicalName: string,
  currentLocale?: string | null,
): string {
  return categoriesStore.getLocalizedCategoryName(categoryTechnicalName, currentLocale);
}

/**
 * Get localized subcategory name from a classification
 * categoryTechnicalName is like "housing-property"
 * subCategoryTechnicalName is like "water"
 * Pass currentLocale from the template ($locale) to ensure Svelte reactivity
 */
export function getLocalizedClassificationSubCategoryName(
  categoryTechnicalName: string,
  subCategoryTechnicalName: string,
  currentLocale?: string | null,
): string {
  return categoriesStore.getLocalizedSubCategoryName(
    categoryTechnicalName,
    subCategoryTechnicalName,
    currentLocale,
  );
}

/**
 * Returns a single display string for the category + optional subcategory.
 *
 * - Catch-all pair (miscellaneous / other): returns the locale key
 *   "categories.catch_all" (DE "Sonstiges", EN "Other") — a single label
 *   with no separator, avoiding the raw "other" technical key leaking into
 *   the UI.
 * - Normal category with subcategory: returns "Category › SubCategory".
 * - Category without subcategory: returns just the category label.
 * - No classification: returns "—".
 *
 * Pass currentLocale from the template ($locale) to ensure Svelte reactivity.
 */
export function getLocalizedCombinedCategoryLabel(
  categoryTechnicalName: string | null | undefined,
  subCategoryTechnicalName: string | null | undefined,
  currentLocale?: string | null,
): string {
  if (!categoryTechnicalName) return '—';

  if (isCatchAllClassification(categoryTechnicalName, subCategoryTechnicalName)) {
    // Resolve via svelte-i18n store so DE/EN toggles work without a page reload.
    return get(_)('categories.catch_all');
  }

  const cat = categoriesStore.getLocalizedCategoryName(categoryTechnicalName, currentLocale);
  const sub = subCategoryTechnicalName
    ? categoriesStore.getLocalizedSubCategoryName(
        categoryTechnicalName,
        subCategoryTechnicalName,
        currentLocale,
      )
    : null;

  return sub ? `${cat} › ${sub}` : cat;
}
